import './application-calculation-summary.scss'

import {
  CircularProgress,
} from '@mui/material'
import sortBy from 'lodash/sortBy'
import { useTranslation } from 'react-i18next'
import isEmpty from 'lodash/isEmpty'

import {
  useDeclarationsByOrderIdQuery,
  useGetBankCommissionsByOrderIdQuery,
  useGetBrokerDataByCodeQuery,
  useGetExtraServicesByOrderIdQuery,
  useGetOrderSummaryByIdQuery,
  useGetOrderSummaryInvoicesByIdQuery,
  useGetRouteByOrderIdQuery,
  useInvoicesByOrderIdQuery,
  useOrderByIdQuery,
  useSummaryByOrderIdQuery,
} from 'api/calc'
import {
  useApplicationCalculationData,
} from 'api/applications'
import SummaryCalculator from 'components/common/summaryCalculator'
import { getApplicationCalculationExtraInfo } from '../calculation-extra-info'
import { getApplicationComparisonPresentation } from '../calculation-comparison-presentation'

const ApplicationCalculationSummary = ({
  orderId,
  tnvedReviews,
  tnvedChangesConfirmed,
  calculationComparison,
  calculationSnapshotProductCodes,
  hasProposalAdjustment,
  calculationCurrencyCode,
  onCollapse,
}: ApplicationCalculationSummaryProps) => {
  const { t } = useTranslation()
  const skip = !orderId

  const { isLoading: isOrderLoading } = useOrderByIdQuery(orderId, { skip })
  const { isLoading: isPackagingLoading } = useSummaryByOrderIdQuery(orderId, { skip })
  const { isLoading: isSummaryLoading } = useGetOrderSummaryByIdQuery(orderId, { skip })
  const { isLoading: isInvoicesLoading } = useGetOrderSummaryInvoicesByIdQuery(orderId, { skip })
  const { isLoading: isOrderInvoicesLoading } = useInvoicesByOrderIdQuery(orderId, { skip })
  const { isLoading: isRouteLoading } = useGetRouteByOrderIdQuery(orderId, { skip })
  const { isLoading: isDeclarationsLoading } = useDeclarationsByOrderIdQuery(orderId, { skip })
  const { isLoading: isBankLoading } = useGetBankCommissionsByOrderIdQuery({ orderId }, { skip })
  const { isLoading: isCertLoading } = useGetBrokerDataByCodeQuery(orderId, { skip })
  const { isLoading: isExtraLoading } = useGetExtraServicesByOrderIdQuery(orderId, { skip })

  const calculation = useApplicationCalculationData(orderId)
  const {
    order,
    packagingSummary,
    summaryInfo,
    invoices,
    route,
    declarations,
    bankCommissions,
    certification,
    extraServices,
  } = calculation ?? {}

  const isLoading = [
    isOrderLoading,
    isPackagingLoading,
    isSummaryLoading,
    isInvoicesLoading,
    isOrderInvoicesLoading,
    isRouteLoading,
    isDeclarationsLoading,
    isBankLoading,
    isCertLoading,
    isExtraLoading,
  ].some(Boolean)

  if (isLoading && !order) {
    return (
      <div className='application-calculation-summary application-calculation-summary_loading'>
        <CircularProgress size={28} />
      </div>
    )
  }

  const packaging = packagingSummary ?? summaryInfo?.packaging
  const summaryDelivery = route ?? (
    summaryInfo?.delivery && 'freights' in summaryInfo.delivery ? summaryInfo.delivery : undefined
  )

  return (
    <div className='application-calculation-summary'>
      <SummaryCalculator
        calculation={{
          ...getApplicationComparisonPresentation({
            calculationComparison,
            calculationCurrencyCode: calculationCurrencyCode || order?.base_currency,
            hasProposalAdjustment,
            bankCommissions: bankCommissions ?? order?.bankCommissions,
            translate: t,
          }),
          order: order ? {
            ...order,
            bankCommissions: bankCommissions ?? order.bankCommissions,
            userExtraServices: extraServices ?? order.userExtraServices,
          } : undefined,
          invoices: invoices ? sortBy(invoices, 'id') : undefined,
          summaryOrder: packaging ? {
            ...packaging,
            ...summaryInfo?.summary,
            containers_user: !isEmpty(packaging.containers_manual)
              ? packaging.containers_manual
              : packaging.containers,
          } : undefined,
          summaryCustoms: summaryInfo?.customs ?? order?.summaryCustoms,
          summaryDelivery,
          summaryCompleted: {
            ...packaging?.completed,
            ...order?.summaryCompleted,
            ...summaryInfo?.completed,
          },
          declarations,
          summaryCertification: certification,
          extraInfo: getApplicationCalculationExtraInfo({
            calculation: calculation ?? {},
            tnvedReviews,
            tnvedChangesConfirmed,
            calculationSnapshotProductCodes,
            translate: t,
          }),
        }}
        noLimeColor
        presentation={{
          totalInHeader: true,
          collapsible: true,
          onCollapse,
          defaultExpandedAll: true,
          hideStatusIcons: true,
          showAllInfo: true,
        }}
      />
    </div>
  )
}

export default ApplicationCalculationSummary
